import Box from 'UIKit/Box/Box'
import Flex from 'UIKit/Box/Flex'
import IconButton from 'UIKit/Button/IconButton'
import { NextLinkFromReactRouter } from 'UIKit/NextLink'
import OpenEffect from 'UIKit/OpenEffect'
import Page from 'UIKit/Page'
import Text from 'UIKit/Text/Text'
import { RouteConfig } from 'config/constants/route'
import { Game, GameDemoSession, GameDetail, GameType, InhouseGameDetails } from 'config/types/game'
import useDebounce from 'hooks/useDebounce'
import useForceUpdate from 'hooks/useForceUpdate'
import { useVariableLoadedListener } from 'hooks/useVariableLoadedListener'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useAuth } from 'state/auth/hooks'
import styled, { css } from 'styled-components'
import { Icons } from 'svgs'
import colors from 'theme/ui/colors'
import { formatRoute } from 'utils/stringHelper'
import AirdropAnimation from 'views/GameDetails/AirdropAnimation'
import InhouseGame from 'views/InhouseGame'

import { useSidebarInfo } from 'state/app/hooks'
import GameDesktopIframe from './GameDesktopIframe'
import LowWagerMultiplierAwareBadge from './LowWagerMultiplierAwareBadge'

export interface GameDesktopViewProps {
  game: Game
  gameDemoSession: GameDemoSession
  isMinimize?: boolean
  onClose?: () => void
}

export interface GameDesktopIframePopupProps {
  gameDetail?: GameDetail
  gameDemoSession?: GameDemoSession
}

const GameDesktopIframePopup: React.FC<GameDesktopIframePopupProps> = ({ gameDetail, gameDemoSession }) => {
  const gameRef = useRef(
    gameDetail
      ? {
          code: gameDetail.code,
          game: gameDetail,
          gameDemoSession,
        }
      : null,
  )

  const isMiniScreenDisabled = useRef(false)
  const containerRef = useRef<HTMLDivElement>()
  const container = useVariableLoadedListener(() => containerRef.current)
  const { dependency, update: forceUpdate } = useForceUpdate()
  const [miniScreenClosing, setMiniScreenClosing] = useState(false)
  const { isSigned } = useAuth()
  const { sidebarWidth } = useSidebarInfo()

  useEffect(() => {
    if (miniScreenClosing) {
      setTimeout(() => {
        gameRef.current = null
        setMiniScreenClosing(false)
        forceUpdate()
      }, 400)
    }
  }, [miniScreenClosing])

  const close = () => {
    isMiniScreenDisabled.current = true
  }

  const gameInfo = useMemo(() => {
    if (isMiniScreenDisabled.current && !gameDetail) {
      gameRef.current = null
      isMiniScreenDisabled.current = false
      return gameRef.current
    }

    if (!gameDetail || gameDetail.code === gameRef.current?.code) {
      return gameRef.current
    }

    gameRef.current = {
      code: gameDetail.code,
      game: gameDetail,
      gameDemoSession,
    }

    return gameRef.current
  }, [gameDetail, dependency])

  const enableMiniscreen = useMemo(() => {
    return !gameDetail
  }, [gameDetail])

  const miniScreenEnabled = useDebounce(enableMiniscreen, 800)

  useEffect(() => {
    const airdropAnimatedElement = document.getElementById('airdrop-parachute')
    if (!airdropAnimatedElement) return
    if (miniScreenEnabled && enableMiniscreen) airdropAnimatedElement.style.opacity = '0'
    else airdropAnimatedElement.style.opacity = '1'
  }, [miniScreenEnabled, enableMiniscreen])

  const screenInfo = useMemo(() => {
    const rootLayout = document.getElementById('root-layout')
    const screenWidth = (rootLayout && rootLayout.offsetWidth) || window.innerWidth
    return (
      container && {
        fixedRight: `${screenWidth - container.getBoundingClientRect().right}px`,
        width: enableMiniscreen ? `${container.clientWidth}px` : '100%',
      }
    )
  }, [enableMiniscreen, container])

  const isDisplay = !!container && !!gameInfo?.game

  return (
    <Box
      maxWidth="1410px"
      width="100%"
      m="auto"
      p={['0px 12px', '0px 12px', '0px 16px', '0px 24px', '0px 24px', '0px 24px', '0px 24px', '0px 80px']}
    >
      <Box ref={containerRef} width="100%" />

      {isDisplay && (
        <StyledContainer
          className="animate"
          m={['20px 0px', '20px 0px', '20px 0px', '40px 0px']}
          p="0px !important"
          $enableMiniScreen={enableMiniscreen}
          width={screenInfo.width}
          $fixedRight={screenInfo.fixedRight}
          $isClosed={miniScreenClosing}
          $sidebarWidth={sidebarWidth}
        >
          <LowWagerMultiplierAwareBadge game={gameInfo?.game} />
          <Box position="relative">
            {miniScreenEnabled && enableMiniscreen && (
              <OpenEffect
                openType="grow"
                position="absolute"
                height="140px"
                width="100%"
                background={colors.modalHeaderBackground}
                bottom="calc(100% - 10px)"
                borderTopLeftRadius="20px"
                borderTopRightRadius="20px"
                overflow="hidden"
              >
                <Flex justifyContent="space-between" alignItems="center" mt="36px" mr="24px">
                  <Text ellipsis={1} letterSpacing="-2%" lineHeight="1.2" color="text" fontSize="46px" bold ml="24px">
                    {gameInfo.game.name}
                  </Text>

                  <IconButton
                    onClick={() => {
                      setMiniScreenClosing(true)
                    }}
                  >
                    <Icons.ClearIcon fill="#fff" height="60px" width="60px" />
                  </IconButton>
                </Flex>
              </OpenEffect>
            )}

            <StyledGameContainer>
              {gameInfo.game.type === GameType.InHouse ? (
                <InhouseGame game={gameInfo.game as InhouseGameDetails} isMinimize={enableMiniscreen} />
              ) : (
                <GameDesktopIframe
                  onClose={close}
                  isMinimize={enableMiniscreen}
                  game={gameInfo.game}
                  gameDemoSession={gameInfo.gameDemoSession}
                />
              )}

              {miniScreenEnabled &&
                enableMiniscreen &&
                (gameInfo.game.type === GameType.InHouse ? (
                  <StyledFullScreen as={NextLinkFromReactRouter} to={gameInfo.game?.slug} />
                ) : (
                  <StyledFullScreen
                    as={NextLinkFromReactRouter}
                    to={formatRoute(RouteConfig.GameDetail, { slug: gameInfo.game?.slug })}
                  />
                ))}
            </StyledGameContainer>
          </Box>
          {isSigned && !enableMiniscreen && <AirdropAnimation />}
        </StyledContainer>
      )}
    </Box>
  )
}

const StyledFullScreen = styled(Box)`
  position: absolute;
  width: 100%;
  top: 0px;
  height: 100%;
  opacity: 1;
  cursor: pointer;
`

const StyledGameContainer = styled(Box)`
  position: relative;
`

const StyledContainer = styled(Page)<{
  $enableMiniScreen?: boolean
  $fixedRight?: string
  $isClosed?: boolean
  $sidebarWidth?: number
}>`
  position: ${({ $enableMiniScreen }) => ($enableMiniScreen ? 'fixed' : 'static')};
  z-index: 3;
  height: auto;
  margin-top: 12px !important;
  margin-bottom: 0px;

  bottom: 12px;
  right: ${({ $fixedRight }) => $fixedRight};
  opacity: 1;

  ${({ $enableMiniScreen, $sidebarWidth }) =>
    $enableMiniScreen
      ? css`
          &.animate {
            max-height: calc(100% - 64px - 48px);
            z-index: 5;
            transition: right 0.2s, transform 0.6s;
            right: calc(12px + ${$sidebarWidth}px);
            transform-origin: right bottom;
            transform: scale(0.3);
          }
        `
      : ''};

  ${({ $isClosed }) =>
    $isClosed
      ? css`
          pointer-events: none;
          transition: 0.4s !important;
          right: -64px !important;
          opacity: 0;
        `
      : css``};
`

export default GameDesktopIframePopup
